import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@workspace/ui/components/Spinner';
import { Typography } from '@workspace/ui/components/Typography';

/**
 * Octo payment return page. Renders inside the Octo iframe (web) or as a
 * top-level page in a Custom Tab / SafariView (RN).
 *
 * - Web (iframe): posts an `octoPaymentReturn` message to the parent window;
 *   the parent (CheckoutContent) closes the iframe and finalises the order.
 * - RN (top-level): triggers a deeplink so the host RN app is brought to
 *   foreground and forwards the result to the WebView. Android uses the
 *   `intent://...#Intent;scheme=jiffyapp;end` form to avoid Chrome's
 *   unknown-scheme fallback; iOS uses plain `jiffyapp://...`.
 *   A short fallback navigation handles direct browser hits without the app.
 */
const Complete = () => {
  const { t } = useTranslation();
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get('orderId') || undefined;
    // Octo passes the failure signal as `octo_status` (e.g. `canceled`) for
    // the bind-card flow; keep `status` as the normalized field consumers see.
    const status =
      params.get('status') || params.get('octo_status') || undefined;
    if (status === 'canceled' || status === 'failed') {
      setErrored(true);
    }

    if (window.parent !== window) {
      window.parent.postMessage(
        { event: 'octoPaymentReturn', orderId, status },
        window.location.origin,
      );
      return;
    }

    const query = new URLSearchParams();
    if (orderId) query.set('orderId', orderId);
    if (status) query.set('status', status);
    const queryString = query.toString();
    const querySuffix = queryString ? `?${queryString}` : '';

    // On Android Chrome Custom Tab, `window.location.href = 'jiffyapp://...'`
    // makes Chrome attempt to fetch the unknown-scheme URL as `http://complete/`
    // before firing the intent — flashing a "could not be loaded" error.
    // The `intent://` form is the documented Chrome mechanism: it parses the
    // intent and launches the app without any URL fetch. The OS still routes
    // the intent to our app with data `jiffyapp://complete?...`, so the RN-side
    // `Linking` listener sees the same URL it would have seen otherwise.
    const isAndroid = /android/i.test(navigator.userAgent);
    const deeplinkUrl = isAndroid
      ? `intent://complete${querySuffix}#Intent;scheme=jiffyapp;end`
      : `jiffyapp://complete${querySuffix}`;
    window.location.href = deeplinkUrl;

    // Bind-card flow has no orderId. We don't want to fall back to `/`,
    // since on iOS SafariViewController this leaves the user staring at the
    // homepage inside the in-app browser. The host RN app finalizes the
    // bind-card flow via methods/get polling and explicit closeInAppBrowser.
    if (!orderId) return;

    const fallback = setTimeout(() => {
      window.location.replace(`/order/${orderId}`);
    }, 2000);

    // Cancel the fallback when the page is being torn down (deeplink succeeded
    // and the host app foregrounded — the Custom Tab is going away). Prevents
    // a stale `replace()` from firing analytics / `onPaymentSuccess` twice.
    const cancelFallback = () => clearTimeout(fallback);
    window.addEventListener('pagehide', cancelFallback);

    return () => {
      clearTimeout(fallback);
      window.removeEventListener('pagehide', cancelFallback);
    };
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6">
      <Spinner size="xl" className="text-icon-brand" />
      <Typography.Text100Regular as="p" className="text-center">
        {errored ? t('phrases:errorOccurred') : t('pleaseWait')}
      </Typography.Text100Regular>
    </div>
  );
};

export default Complete;
